import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Modal } from 'antd';

import 'react-calendar-timeline/style.css';
import {
  Timeline,
  TimelineMarkers,
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  CustomMarker,
} from 'react-calendar-timeline';

import styles from './styles.module.scss';

import { DateFormat, InitialEditLocationFields, TileMinZoom } from '../../constants/data';
import { useGetNotesQuery } from '../../store/api/notes';
import { useGetFieldsQuery } from '../../store/api/fields';
import { useGetLocationsQuery } from '../../store/api/sensors';
import Panel from '../Panel/index.jsx';
import { CropsSelect } from '../CropsSelect/CropsSelect.jsx';
import { EmptyView } from '../EmptyView/index.jsx';
import { NotesPanel, NoteButton } from '../Notes/index.jsx';
import { Loading } from '../index.jsx';
import { getDatesBetween, groupDataByMonth } from '../../utils/dates';
import { getCropColorById, getCropName, getWorkspaceCrops } from '../../utils/crops';
import { FilterOrderButton } from '../Filters/index.jsx';
import { GanttFilterZoom } from './index.jsx';
import { TimeLineMaxZoom, TimeLineMinZoom } from './Gantt.constants.jsx';
import { CustomLabelFormat, CustomTopLabelFormat } from './Gantt.lib.jsx';
import useOnClickLocation from './hooks/useOnClickLocation.jsx';
import useEditOrDuplicateSeason from '../Fields/hooks/useOnClickEditSeason.jsx';
import { GetSeasonDisabledDates } from '../Fields/utils/index.jsx';
import AddPeriodForm from '../AddPeriodForm/index.jsx';
import { GanttEditLocationButtonObject, GanttEditSeasonButton } from './utils/index.jsx';
import { GanttItemHeight, GanttItemInnerHeight } from './constants/index.jsx';
import { SidebarFieldHeader, SidebarItem, SidebarTitle } from '../Sidebar/index.jsx';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

const Gantt = ({ t }) => {
  const navigate = useNavigate();

  const { crops } = useSelector((state) => state.catalog);
  const {
    sorting: { dir, crop },
  } = useSelector((state) => state.sensors);
  const { currentUserRole } = useSelector((state) => state.auth);

  const {
    data: fields,
    isSuccess: isFieldsSuccess,
    isFetching: isFieldsLoading,
    isError: isFieldsError,
    error: fieldsError,
  } = useGetFieldsQuery();

  const {
    data: locations,
    isSuccess: isLocationsSuccess,
    isFetching: isLocationsLoading,
    isError: isLocationsError,
    error: locationsError,
  } = useGetLocationsQuery();
  const { data: notesData, isSuccess: isNotesDataSuccess } = useGetNotesQuery();

  const today = dayjs().set('hour', 12);

  const onClickLocation = useOnClickLocation({ locations });

  // Edit/duplicate season
  const onEditSeason = useEditOrDuplicateSeason();

  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [items, setItems] = useState([]);

  const [workspaceCrops, setWorkspaceCrops] = useState([]);
  const [filterCrop, setFilterCrop] = useState({ id: crop, color: '' });

  const [ganttKey, setUpdateGanttKey] = useState(0);

  const [currentLocation, setCurrentLocation] = useState([]);
  const [currentLocationCrop, setCurrentLocationCrop] = useState({ id: crop, color: '' });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [notes, setNotes] = useState([]);

  const [zoomUnit, setZoomUnit] = useState('month'); //day || month
  const [defaultTimeStart, setDefaultTimeStart] = useState(dayjs().add(-22, 'month').set('hour', 0).valueOf());
  const [defaultTimeEnd, setDefaultTimeEnd] = useState(dayjs().add(2, 'month').set('hour', 0).valueOf());
  // month/day
  // useState(dayjs().add(-1, 'month').set('hour', 0).valueOf());
  // useState(dayjs().add(2, 'day').set('hour', 0).valueOf());
  // year/month
  // useState(dayjs().add(-10, 'month').set('hour', 0).valueOf());
  // useState(dayjs().add(2, 'month').set('hour', 0).valueOf());

  const [activeNotesPanel, setActiveNotesPanel] = useState(false);
  const [activeEditLocationPanel, setEditLocationPanel] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(InitialEditLocationFields);
  const [selectedSensor, setSelectedSensor] = useState({ id: '', name: '' });

  const [errorMessage, setErrorMessage] = useState('');

  const onZoomTimeline = (timelineContext, unit) => {
    setZoomUnit(unit);
  };

  const onClickZoomButton = ({ unit }) => {
    //day || month
    if (unit === zoomUnit) return;

    // week
    let startDay;
    let endDay;

    if (unit === 'day') {
      // month
      startDay = dayjs().add(-1, 'month').set('hour', 0);
      endDay = dayjs().add(2, 'day').set('hour', 0);
    } else if (unit === 'month') {
      // year
      startDay = dayjs().date(1).set('hour', 0).add(-22, 'month');
      endDay = dayjs().date(1).set('hour', 0).add(2, 'month');
    }

    setZoomUnit(unit);
    setDefaultTimeStart(startDay.valueOf());
    setDefaultTimeEnd(endDay.valueOf());
  };

  const openNotesPanel = ({ notesLocation, location, date }) => {
    setUpdateGanttKey(ganttKey + 1);
    setActiveNotesPanel(true);
    setCurrentLocation(location);
    const selectedNotes = zoomUnit === 'month' ? notesLocation : notesLocation.filter((obj) => obj.date === date);
    setNotes(selectedNotes);
    setCurrentDate(date);

    fields.map((item) => {
      const seasons = item.seasons.filter((season) => season.id === location.field_season_id);
      if (seasons && seasons.length) {
        setCurrentLocationCrop({
          id: seasons[0].crop_id,
          color: getCropColorById(seasons[0].crop_id, crops),
        });
      }
    });
  };

  const onOpenEditLocation = (location) => {
    if (!location || !location.sensor) return;

    setSelectedLocation({
      locationId: location.id,
      seasonId: location.field_season_id,
      name: location.name,
      install: location.install_date,
      removal: location.removal_date,
      // coordinates: location.sensor.gps_point
      //   ? location.sensor.gps_point.coordinates
      //   : location.install_point.coordinates,
      coordinates: location.install_point ? location.install_point.coordinates : location.sensor.gps_point.coordinates,
    });
    setSelectedSensor({ id: location.sensor.id, name: location.sensor.name });
    setEditLocationPanel(true);
  };

  // Toggle group visibility
  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };

  // Filter groups: Show only parent groups and expanded children
  const visibleGroups = groups.filter((group) => !group.parent || expandedGroups.includes(group.parent));

  // Filter items: Show only items of visible groups
  const visibleItems = items.filter((item) => visibleGroups.some((group) => group.id === item.group));
  const selectedItems = visibleItems.map((item) => item.id);

  // Custom group renderer to show nesting and expand/collapse buttons
  const groupRenderer = ({ group }) => {
    const isExpandable = groups.some((g) => g.parent === group.id);
    const isExpanded = expandedGroups.includes(group.id);

    return (
      <div
        className={`
        ${group.level === 0 ? styles.ganttGroup : ''} 
        ${group.level === 1 ? styles.ganttGroupExtanded : ''}
        ${group.level === 2 ? styles.ganttGroupExtraHeader : ''}
        ${group.level === 3 ? styles.ganttGroupExtra : ''}`}
      >
        {isExpandable && (
          <button
            className={`${styles.ganttGroupButton} ${isExpanded ? styles.ganttGroupButtonTop : ''}`}
            onClick={() => toggleGroup(group.id)}
          >
            <CroplerIcon name="down" />
          </button>
        )}
        {group.title}
      </div>
    );
  };

  // Error/Empty message
  useEffect(() => {
    if (isFieldsError && fieldsError && fieldsError.length) {
      setErrorMessage(fieldsError[0].detail);
    } else if (isLocationsError && locationsError && locationsError.length) {
      setErrorMessage(locationsError[0].detail);
    }
  }, [isFieldsError, fieldsError, isLocationsError, locationsError]);

  // Crops list for filter
  useEffect(() => {
    if (!isFieldsSuccess || !crops) {
      return;
    }
    setWorkspaceCrops(getWorkspaceCrops({ fields, crops }));
  }, [isFieldsSuccess, fields, crops]);

  // Get groups and items
  useEffect(() => {
    if (!isFieldsSuccess || !isLocationsSuccess || !isNotesDataSuccess || !fields || !locations) return;

    const filteredGroups = [];
    const filteredItems = [];
    let filteredFields = [];

    // Filter/Sorting
    const fieldsWithSeasons = [...fields].filter((item) => item.seasons.length);
    const sortingFields = fieldsWithSeasons.sort((a, b) =>
      dir === '-' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name),
    );
    if (filterCrop.id !== 'all') {
      sortingFields.map((item) => {
        const seasonFilteredCrop = item.seasons.filter((obj) => obj.crop_id === filterCrop.id);
        if (seasonFilteredCrop.length) {
          filteredFields.push({
            ...item,
            fieldId: item.id,
            seasons: seasonFilteredCrop,
          });
        }
      });
    } else {
      filteredFields = sortingFields.map((item) => ({
        ...item,
        fieldId: item.id,
      }));
    }

    // Get Groups/Items lists
    let expandedGroupsList = [];
    filteredFields.map((item) => {
      let itemLocations = [];
      const itemSeasons = item.seasons;

      // Current season for this field
      let currentItemSeason;
      if (itemSeasons && itemSeasons.length) {
        const filteredItemSeasons = itemSeasons.filter(
          (item) => dayjs(item.harvest_date).isSame(today) || dayjs(item.harvest_date).isAfter(today),
        );
        currentItemSeason = filteredItemSeasons.length ? filteredItemSeasons[0] : itemSeasons[itemSeasons.length - 1];
      }

      // Groups level 0
      filteredGroups.push({
        id: item.id,
        title: (
          <SidebarFieldHeader
            title={item.name}
            crop={currentItemSeason ? currentItemSeason.crop_id : ''}
            startDate={currentItemSeason ? currentItemSeason.sowing_date : ''}
            endDate={currentItemSeason ? currentItemSeason.harvest_date : ''}
            isExpandable={true}
            t={t}
          />
        ),
        level: 0,
        height: GanttItemHeight,
        stackItems: false,
      });

      // Groups level 1
      if (locations.length && itemSeasons.length) {
        itemSeasons.map((season) => {
          const seasonLocations = locations.filter((location) => location.field_season_id === season.id);
          if (seasonLocations.length) {
            itemLocations = [...itemLocations, ...seasonLocations];
          }
        });

        if (itemLocations.length) {
          itemLocations.map((location) => {
            filteredGroups.push({
              id: location.id,
              title: (
                <div className={styles.ganttGroupSubtitle}>
                  <SidebarItem title={location.name || t('empty.noName')} t={t} />
                </div>
              ),
              level: 1,
              parent: item.id,
              height: GanttItemInnerHeight,
              stackItems: false,
            });
          });
        }
      }

      // Items level 0
      if (itemSeasons.length) {
        itemSeasons.map((season) => {
          filteredItems.push({
            id: season.id,
            group: item.id,
            title: (
              <div
                className={styles.ganttLineWrapper}
                style={{ color: crops.filter((c) => c.id === season.crop_id)[0].color }}
                title=""
              >
                <div
                  className={`
                ${styles.ganttLine} 
                ${currentUserRole === 'owner' || currentUserRole === 'editor' ? styles.ganttLineWithEdit : ''}
                `}
                >
                  <span className={styles.ganttLineText}>{getCropName({ t, id: season.crop_id })}</span>
                </div>
              </div>
            ),
            canMove: false,
            canResize: false,
            height: 60,
            start_time: dayjs(season.sowing_date),
            end_time: dayjs(season.harvest_date || today),
          });

          // Edit season button
          if (currentUserRole === 'owner' || currentUserRole === 'editor') {
            filteredItems.push(
              GanttEditSeasonButton({
                id: item.id,
                season,
                today,
                onClickButton: () =>
                  onEditSeason({
                    season,
                    fieldId: item.id,
                    isEdit: true,
                    currentZoom: TileMinZoom,
                    disabledDates: GetSeasonDisabledDates({
                      fields,
                      id: item.id,
                      seasonId: season.id,
                      isEdit: true,
                    }),
                  }),
              }),
            );
          }
        });
      }

      // Items level 1 / Notes
      if (itemLocations.length) {
        itemLocations.map((location) => {
          filteredItems.push({
            id: 'location' + location.id,
            group: location.id,
            title: (
              <div
                className={`${styles.ganttLineWrapper} ${styles.ganttLineWrapperExtended} rct-item-content-item-extended`}
                title=""
              >
                <div
                  className={`${styles.ganttLine} ${styles.ganttLineExtended} ${currentUserRole === 'owner' || currentUserRole === 'editor' ? styles.ganttLineWithEdit : ''}`}
                >
                  <span className={`${styles.ganttLineText} ${styles.ganttLineTextExtended}`}>
                    {location.name ? location.name : t('empty.noName')}
                  </span>
                </div>
              </div>
            ),
            canMove: false,
            canResize: false,
            className: 'rct-item--location',
            start_time: dayjs(location.install_date),
            end_time: dayjs(location.removal_date),
          });

          // Edit location button
          if (currentUserRole === 'owner' || currentUserRole === 'editor') {
            filteredItems.push(
              GanttEditLocationButtonObject({
                location,
                onClickButton: () => onOpenEditLocation(location),
              }),
            );
          }
        });

        // if (!expandedGroupsList.length) {
        expandedGroupsList.push(item.id);
        // }

        // Items Notes
        if (notesData.length) {
          itemLocations.map((location) => {
            const notesLocation = notesData.filter((obj) => obj.location_id === location.id);
            // const lastNote = notesLocation[notesLocation.length - 1];
            const crop = itemSeasons.filter((s) => s.id === location.field_season_id)[0].crop_id;
            const cropColor = getCropColorById(crop, crops);

            // Update notes panel if it's opened
            if (activeNotesPanel && currentLocation.id === location.id) {
              const selectedNotes =
                zoomUnit === 'month' ? notesLocation : notesLocation.filter((obj) => obj.date === currentDate);
              setNotes(selectedNotes);
            }

            if (notesLocation.length) {
              // Notes buttons for zoomUnit === day
              const periodDaysArray = getDatesBetween(location.install_date, location.removal_date);
              periodDaysArray.map((obj, index) => {
                const note = notesLocation.filter((n) => n.date === obj);
                if (note.length) {
                  filteredItems.push({
                    id: 'note-' + location.id + index,
                    group: item.id,
                    title: (
                      <div className={styles.ganttItemNav} title="">
                        <NoteButton
                          type={note[0].note_type}
                          color={cropColor}
                          count={note.length}
                          onButtonClick={() => openNotesPanel({ notesLocation, location, date: obj })}
                          t={t}
                        />
                      </div>
                    ),
                    canMove: false,
                    canResize: false,
                    start_time: dayjs(obj).set('hour', 0),
                    end_time: dayjs(obj).set('hour', 24),
                  });
                }
              });

              // Notes buttons (for every month) for zoomUnit === month
              const groupsNotes = groupDataByMonth(notesLocation);
              groupsNotes.map((objMonth, index) => {
                filteredItems.push({
                  id: 'note-month' + location.id + index,
                  group: item.id,
                  title: (
                    <div className={styles.ganttItemNavYear} title="">
                      <NoteButton
                        type={objMonth.data[0].note_type}
                        color={cropColor}
                        count={objMonth.data.length}
                        onButtonClick={() =>
                          openNotesPanel({ notesLocation: objMonth.data, location, date: objMonth.date })
                        }
                        t={t}
                      />
                    </div>
                  ),
                  canMove: false,
                  canResize: false,
                  start_time: dayjs(objMonth.month).set('hour', 0),
                  end_time: dayjs(objMonth.month).set('hour', 24),
                });
              });
            }
          });
        }
      }
    });

    // Unbound sensors
    const locationsNoSeason = [];
    locations.map((location) => {
      if (!location.field_season_id) {
        locationsNoSeason.push({ location });
      }
    });

    if (locationsNoSeason.length) {
      // Unbound sensors header
      filteredGroups.push({
        id: 'unbound-sensors',
        title: <div className={styles.ganttGroupHeader}>{t('sensor.unboundSensors')}</div>,
        level: 2,
        height: 48,
        stackItems: false,
      });

      locationsNoSeason.map((item) => {
        const location = item.location;
        filteredGroups.push({
          id: location.id,
          title: (
            <SidebarItem
              title={location.name || t('empty.noName')}
              showAddSeasonButton={true}
              onClickAddSeason={() => navigate('/fields')}
              t={t}
            />
          ),
          level: 3,
          height: GanttItemInnerHeight,
          stackItems: false,
        });
        filteredItems.push({
          id: 'location' + location.id,
          group: location.id,
          title: (
            <div
              className={`${styles.ganttLineWrapper} ${styles.ganttLineWrapperExtended} rct-item-content-item-extended`}
              title=""
            >
              <div className={`${styles.ganttLine} ${styles.ganttLineExtended}`}>
                <span className={`${styles.ganttLineText} ${styles.ganttLineTextExtended}`}>
                  {location.name ? location.name : t('empty.noName')}
                </span>
              </div>
            </div>
          ),
          canMove: false,
          canResize: false,
          className: 'rct-item--location',
          start_time: dayjs(location.install_date),
          end_time: dayjs(location.removal_date),
        });
        // Edit location button
        if (currentUserRole === 'owner' || currentUserRole === 'editor') {
          filteredItems.push(
            GanttEditLocationButtonObject({
              location,
              onClickButton: () => onOpenEditLocation(location),
            }),
          );
        }
      });
    }

    setGroups(filteredGroups);
    setItems(filteredItems);
    setUpdateGanttKey(ganttKey + 1);
    setExpandedGroups(expandedGroupsList);
  }, [isFieldsSuccess, isLocationsSuccess, isNotesDataSuccess, filterCrop, dir]);

  const onChangeFilterCrop = (value) => {
    setFilterCrop(value);
    setUpdateGanttKey(ganttKey + 1);
  };

  if (isFieldsLoading || isLocationsLoading) {
    return <Loading />;
  }

  return (
    <>
      {items.length > 0 ? (
        <>
          <div className={styles.ganttFilter}>
            <div className={styles.ganttFilterSidebar}>
              <div className={styles.ganttFilterSelectContainer}>
                <CropsSelect
                  isFilter={true}
                  cropsList={workspaceCrops}
                  currentCrop={crop}
                  changeCrop={onChangeFilterCrop}
                  t={t}
                />
              </div>
              <FilterOrderButton t={t} />
            </div>
            <div className={styles.ganttFilterBody}>
              <GanttFilterZoom zoomUnit={zoomUnit} onClickZoomButton={onClickZoomButton} t={t} />
            </div>
          </div>
          <div className={`${styles.gantt} ${zoomUnit === 'month' ? styles.ganttUnitMonth : ''}`} key={defaultTimeEnd}>
            <Timeline
              key={ganttKey}
              groups={visibleGroups}
              items={visibleItems}
              groupRenderer={groupRenderer}
              sidebarWidth={272}
              minZoom={TimeLineMinZoom}
              maxZoom={TimeLineMaxZoom}
              itemHeightRatio={1}
              lineHeight={GanttItemHeight}
              minResizeWidth={64}
              useResizeHandle={false}
              defaultTimeStart={defaultTimeStart}
              defaultTimeEnd={defaultTimeEnd}
              selected={selectedItems}
              itemTouchSendsClick={true}
              onItemClick={onClickLocation}
              onZoom={onZoomTimeline}
              className="crops"
            >
              <TimelineHeaders calendarHeaderClassName={styles.ganttTimelineHeaders}>
                <SidebarHeader>
                  {() => {
                    return (
                      <div className={styles.ganttTimelineSidebarHeader}>
                        <SidebarTitle title={t('fields.title')} count={fields.length} />
                      </div>
                    );
                  }}
                </SidebarHeader>
                <DateHeader
                  unit={zoomUnit === 'week' ? 'week' : 'primaryHeader'}
                  labelFormat={CustomTopLabelFormat}
                  height={32}
                />
                <DateHeader
                  unit={zoomUnit === 'week' ? 'day' : zoomUnit}
                  labelFormat={CustomLabelFormat}
                  height={24}
                  intervalRenderer={({ getIntervalProps, intervalContext }) => {
                    return (
                      <div
                        {...getIntervalProps()}
                        key={intervalContext.intervalText}
                        className={`${
                          dayjs(intervalContext.interval.startTime).format(DateFormat) === today.format(DateFormat) ||
                          (zoomUnit === 'month' &&
                            dayjs(intervalContext.interval.startTime).format('MM YY') === today.format('MM YY'))
                            ? styles.ganttDateToday
                            : styles.ganttDate
                        }`}
                      >
                        <div>{intervalContext.intervalText}</div>
                      </div>
                    );
                  }}
                />
              </TimelineHeaders>
              <TimelineMarkers>
                <CustomMarker date={today}>
                  {({ styles }) => {
                    const lineStyles = {
                      ...styles,
                      backgroundColor: 'black',
                      width: '1px',
                      marginLeft: '0',
                    };
                    return <div style={lineStyles} />;
                  }}
                </CustomMarker>
              </TimelineMarkers>
            </Timeline>

            {activeNotesPanel && (
              <Panel
                key={notes}
                title={t('notes.title')}
                subtitle={
                  <div className="delimited-text">
                    {currentLocation.name && <span>{currentLocation.name}</span>}
                    {currentLocationCrop && <span>{getCropName({ t, id: currentLocationCrop.id })}</span>}
                  </div>
                }
                setActivePanel={() => setActiveNotesPanel(false)}
              >
                <NotesPanel
                  notes={notes}
                  color={currentLocationCrop.color}
                  locationId={currentLocation.id}
                  date={currentDate}
                  groupByDates={true}
                  t={t}
                />
              </Panel>
            )}
          </div>

          {/*Edit Location*/}
          <Modal
            title={`${t('locations.addPeriod.editFor')} ${selectedSensor.name} ${t('locations.addPeriod.editForSensor')}`}
            open={activeEditLocationPanel}
            onCancel={() => setEditLocationPanel(false)}
            footer={false}
            centered
            width={700}
            className="mobile-hide-modal"
          >
            {activeEditLocationPanel && (
              <AddPeriodForm
                key={selectedLocation.locationId}
                closePanel={() => setEditLocationPanel(false)}
                sensorId={selectedSensor.id}
                initialData={selectedLocation}
                t={t}
              />
            )}
          </Modal>
        </>
      ) : (
        <EmptyView message={errorMessage} t={t} />
      )}
    </>
  );
};

export default Gantt;
